/**
 * @module Router
 *
 */
import React, { useEffect, useMemo, useContext } from "react";
import { AnalyticsService } from "app/services";
import {
  Route,
  Redirect,
  Switch,
  useHistory,
} from "react-router-dom";

/** Routes */
import Benefits from "app/routes/Benefits";
import SignIn from "app/routes/SignIn";
import SignUp from "app/routes/SignUp";
import ForgotPassword from "app/routes/ForgotPassword";
import ResetPassword from "app/routes/ResetPassword";
import Upgrade from "app/routes/Upgrade";
import Trial from "app/routes/Trial";
import PublicProgram from "app/routes/PublicProgram";
import {
  UnsubscribeInvitation,
  UnsubscribeSpace,
  UnsubscribeUser,
} from "app/routes/Unsubscribe";

import PrivateRoute from "./components/Wrappers/PrivateRoute";
import { routerData } from "./RouterData";
import {
  LOCAL_STORAGE_AUTH_TOKEN_NAME,
  LOCAL_STORAGE_AUTH_TOKEN_NAME_TEST,
} from "./components/Sidebar/indexModel";
import useStoreActions from "./utils/hooks/useStoreActions";
import { fetchSelf } from "./actions/user";
import i18n from "app/i18n";
import { getCookie, setCookie } from "./utils/cookie";
import NewSession from "./routes/PublicCalendar/NewSession";
import Confirmation from "./routes/PublicCalendar/Confirmation";
import { UserContext } from "app/contexts/UserContext";
import NewFacilitySession from "./routes/PublicCalendar/NewFacilitySession";
import PublicFacilityPrograms from "./routes/PublicFacilityPrograms";
import StartYourFreeTrial from "./routes/StartYourFreeTrial";
import Purchase from "./routes/Purchase";
import FacilityTimeSlots from "./routes/PublicCalendar/FacilityTimeSlots";
import PaymentConfirmation from "./routes/Sessions/PaymentConfirmation";
import PurchaseConfirmation from "./routes/Purchase/Confirmation";
import PurchaseCancel from "./routes/Purchase/Cancel";
import ProCheckout from "./routes/ProCheckout";
import AnalyzeCheckout from "./routes/AnalyzeCheckout";
import AcademyCheckout from "./routes/AcademyCheckout";
import PublicCoachProfile from "./components/PublicProfiles/PublicCoachProfile";
import PublicFacilityProfile from "./components/PublicProfiles/PublicFacilityProfile";
import QrPage from "./routes/QrPage";
import AcademyWizardWelcome from "./routes/AcademyWizard/Welcome";
import AcademyWizardGeneral from "./routes/AcademyWizard/General";
import AcademyWizardCoachProfile from "./routes/AcademyWizard/CoachProfile";

/**
 * @function AppRouter
 *
 */
const AppRouter = () => {
  const history = useHistory();

  const { user } = useStoreActions({ user: fetchSelf });
  const pending = user.pending || user.data == null;

  useEffect(() => {
    AnalyticsService.page();
  }, [history, user]);

  useMemo(() => {
    let token: string | null = null;

    if (typeof window !== "undefined") {
      try {
        token =
          localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_NAME) ??
          localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_NAME_TEST);
      } catch (e) {
        console.warn("Unable to access localStorage", e);
      }
    }

    if (token && !pending && user && user.data?.locale) {
      i18n.changeLanguage(user.data?.locale);
      setCookie("locale", user.data?.locale, 365);
    } else if (token) {
      const locale = getCookie("locale");
      if (locale && i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
    } else {
      i18n.changeLanguage(
        (navigator.language || navigator.userLanguage).split("-")[0],
      );
    }
  }, [pending, user]);

  const userRole = useContext(UserContext);

  return (
    <Switch>
      {/* Public routes */}
      <Route path="/benefits" component={Benefits} />
      <Route path="/reset" component={ForgotPassword} />
      <Route path="/upgrade" component={Upgrade} />
      <Route path="/start-trial" component={Trial} />
      <Route path="/password/edit" component={ResetPassword} />
      <Route path="/login" component={() => <Redirect to="/signin" />} />
      <Route path="/signin" component={SignIn} />
      <Route
        path={[
          "/signup/:user_role?",
          "/i/:team_token/:team_role?",
          "/invitation/:invitation_token",
          "/l/:invitation_token",
        ]}
        component={SignUp}
      />
      <Route
        path="/public/unsubscribe/invitation/:invitationId"
        component={UnsubscribeInvitation}
      />
      <Route
        path="/public/unsubscribe/user/:token"
        component={UnsubscribeUser}
      />
      <Route
        path="/public/unsubscribe/:spaceId/:token"
        component={UnsubscribeSpace}
      />
      <Route
        path="/public/programs/:program_id/view"
        component={PublicProgram}
      />
      <Route path="/public/availability" component={NewSession} />
      <Route
        path="/public/facility_availability"
        component={NewFacilitySession}
      />
      <Route path="/public/facility_slots" component={FacilityTimeSlots} />
      <Route path="/public/programs" component={PublicFacilityPrograms} />
      <Route path="/public/confirmation" component={Confirmation} />
      <Route path="/pga/start-trial" component={StartYourFreeTrial} />
      <Route path="/trial/analyze" component={AnalyzeCheckout} />
      <Route path="/trial/academy" component={AcademyCheckout} />
      <Route path="/trial/:plan" component={ProCheckout} />
      <Route
        exact
        path={[
          "/purchase/success",
          "/purchase/cancel",
          "/purchase/session/success",
          "/purchase/session/cancel",
        ]}
        component={Purchase}
      />
      <Route
        path="/sessions/payment_confirmation"
        component={PaymentConfirmation}
      />
      <Route
        path="/purchases/:id/confirmation"
        component={PurchaseConfirmation}
      />
      <Route path="/purchases/:id/cancel" component={PurchaseCancel} />
      {/* Direct-purchase (no trial) variants of the trial pages; the sport
          segment only affects signup prefill, the pages ignore it. */}
      <Route
        path="/purchase/analyze"
        render={() => <AnalyzeCheckout purchase />}
      />
      <Route
        path="/purchase/academy"
        render={() => <AcademyCheckout purchase />}
      />
      {/* Exact plan segment, NOT :plan — /purchase/:id (program checkout, in
          routerData below) must keep matching everything else. */}
      <Route path="/purchase/pro" render={() => <ProCheckout purchase />} />
      {/* Public Profile routes */}
      <Route
        path="/facility/:facilitySlug/coach/:coachName/:coachExternalId"
        component={PublicCoachProfile}
      />
      <Route
        path="/facility/:facilityName/:facilityId"
        component={PublicFacilityProfile}
      />
      <Route path="/qr/:token" component={QrPage} />
      {/* Academy onboarding wizard: authenticated full-screen takeover —
          registered here (not in RouterData) so SiteWrapper's sidebar/navbar
          chrome is not rendered around it. Auth is enforced by the
          Authenticated wrapper inside AcademyWizardLayout. */}
      <Route
        exact
        path="/academy/wizard/general"
        component={AcademyWizardGeneral}
      />
      <Route
        exact
        path="/academy/wizard/coach-profile"
        component={AcademyWizardCoachProfile}
      />
      {/* Removed steps (Aug 2026 shortened wizard) — keep old links working. */}
      <Route path="/academy/wizard/(facility|billing|booking-options|availability|almost-there)">
        <Redirect to="/academy/wizard/general" />
      </Route>
      <Route exact path="/academy/wizard" component={AcademyWizardWelcome} />
      {/* Private routes */}
      {routerData.map((route, index) => (
        <Route key={index} path={route.path} exact={route.exact}>
          {route.redirectTo && user.self ? (
            <Redirect from={route.path} to={route.redirectTo(userRole)} />
          ) : (
            <PrivateRoute
              permission={route?.permission || ""}
              permissionObject={route?.permissionObject || ""}
              access={route?.access}
            >
              <route.main />
            </PrivateRoute>
          )}
        </Route>
      ))}
    </Switch>
  );
};

export default AppRouter;
